// src/components/common/PhoneField.jsx
// A phone number with its own country dropdown (India +91 by default) and the number beside it.
// onChange gets the full number as +<code><digits> (e.g. +919876543210) and whether it's valid, so every
// form sends the same shape whatever people type or paste: "98765 43210", "098765 43210",
// "+91 98765-43210" and "919876543210" all become +919876543210.
//   <PhoneField value={phone} onChange={(e164, ok) => ...} inputStyle={...} />   (inline styles)
//   <PhoneField value={phone} onChange={...} className="..." selectClassName="..." />   (Tailwind forms)
import React, { useEffect, useMemo, useState } from "react";

// Countries travellers and partners most often use; India first. min/max: digits after the code.
export const COUNTRIES = [
    { iso: "IN", name: "India", code: "91", flag: "🇮🇳", min: 10, max: 10, re: /^[6-9]\d{9}$/ },
    { iso: "AE", name: "UAE", code: "971", flag: "🇦🇪", min: 8, max: 9 },
    { iso: "US", name: "USA / Canada", code: "1", flag: "🇺🇸", min: 10, max: 10 },
    { iso: "GB", name: "UK", code: "44", flag: "🇬🇧", min: 9, max: 10 },
    { iso: "SG", name: "Singapore", code: "65", flag: "🇸🇬", min: 8, max: 8 },
    { iso: "AU", name: "Australia", code: "61", flag: "🇦🇺", min: 9, max: 9 },
    { iso: "NP", name: "Nepal", code: "977", flag: "🇳🇵", min: 8, max: 10 },
    { iso: "BD", name: "Bangladesh", code: "880", flag: "🇧🇩", min: 10, max: 10 },
    { iso: "LK", name: "Sri Lanka", code: "94", flag: "🇱🇰", min: 9, max: 9 },
    { iso: "BT", name: "Bhutan", code: "975", flag: "🇧🇹", min: 7, max: 8 },
    { iso: "SA", name: "Saudi Arabia", code: "966", flag: "🇸🇦", min: 9, max: 9 },
    { iso: "QA", name: "Qatar", code: "974", flag: "🇶🇦", min: 8, max: 8 },
    { iso: "OM", name: "Oman", code: "968", flag: "🇴🇲", min: 8, max: 8 },
    { iso: "KW", name: "Kuwait", code: "965", flag: "🇰🇼", min: 8, max: 8 },
    { iso: "BH", name: "Bahrain", code: "973", flag: "🇧🇭", min: 8, max: 8 },
    { iso: "MY", name: "Malaysia", code: "60", flag: "🇲🇾", min: 9, max: 10 },
    { iso: "TH", name: "Thailand", code: "66", flag: "🇹🇭", min: 9, max: 9 },
    { iso: "DE", name: "Germany", code: "49", flag: "🇩🇪", min: 10, max: 11 },
    { iso: "FR", name: "France", code: "33", flag: "🇫🇷", min: 9, max: 9 },
    { iso: "NZ", name: "New Zealand", code: "64", flag: "🇳🇿", min: 8, max: 10 },
];
const IN = COUNTRIES[0];
const byLongestCode = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);

/** "+919876543210" or "9876543210" → { country, local } (unknown codes fall back to India) */
export function splitPhone(value, fallback = IN) {
    const raw = String(value || "").trim();
    const d = raw.replace(/\D/g, "");
    if (!d) return { country: fallback, local: "" };
    if (raw.startsWith("+") || raw.startsWith("00")) {
        const digits = raw.startsWith("00") ? d.slice(2) : d;
        const c = byLongestCode.find((x) => digits.startsWith(x.code));
        if (c) return { country: c, local: digits.slice(c.code.length) };
    }
    return { country: fallback, local: cleanLocal(d, fallback) };
}

/** Digits typed for a country, with a trunk 0 or a repeated country code taken off */
export function cleanLocal(digits, country = IN) {
    let d = String(digits || "").replace(/\D/g, "");
    if (d.length > country.max && d.startsWith(country.code)) d = d.slice(country.code.length);   // 91 9876543210
    if (d.length > country.min && d.startsWith("0")) d = d.replace(/^0+/, "");                    // 09876543210
    return d;
}

export const isValidLocal = (local, country = IN) =>
    local.length >= country.min && local.length <= country.max && (!country.re || country.re.test(local));

/** Any typed or saved number → { e164, valid } (India when there's no country code) */
export function normalisePhone(value, fallback = IN) {
    const { country, local } = splitPhone(value, fallback);
    const l = cleanLocal(local, country);
    return { e164: l ? `+${country.code}${l}` : "", valid: isValidLocal(l, country), country, local: l };
}

export const phoneError = (country) => (country.iso === "IN" ? "Enter a 10 digit mobile number starting with 6, 7, 8 or 9." : `Enter a valid ${country.name} number.`);

export default function PhoneField({
    value = "", onChange, inputStyle = null, selectStyle = null, className = "", selectClassName = "",
    placeholder, autoFocus = false, ariaLabel = "Mobile number", id, disabled = false, name,
}) {
    const init = useMemo(() => splitPhone(value), []); // eslint-disable-line react-hooks/exhaustive-deps
    const [country, setCountry] = useState(init.country);
    const [local, setLocal] = useState(init.local);
    // A different number from outside (prefill, a saved trip loading) replaces what's shown
    useEffect(() => {
        const cur = local ? `+${country.code}${cleanLocal(local, country)}` : "";
        if ((value || "") !== cur && normalisePhone(value).e164 !== cur) {
            const s = splitPhone(value, country);
            setCountry(s.country); setLocal(s.local);
        }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    const emit = (c, l) => {
        const clean = cleanLocal(l, c);
        onChange?.(clean ? `+${c.code}${clean}` : "", isValidLocal(clean, c), c);
    };
    const onType = (e) => {
        const typed = e.target.value;
        // Pasting a full number with its code ("+44 7700 900123") also switches the country
        if (/^\s*(\+|00)/.test(typed)) {
            const s = splitPhone(typed, country);
            setCountry(s.country); setLocal(s.local); emit(s.country, s.local); return;
        }
        const keep = typed.replace(/[^\d\s-]/g, "").slice(0, 18);
        setLocal(keep); emit(country, keep);
    };
    const onPick = (e) => {
        const c = COUNTRIES.find((x) => x.iso === e.target.value) || IN;
        setCountry(c); emit(c, local);
    };

    const box = { display: "flex", gap: 6, alignItems: "stretch", minWidth: 0, width: "100%" };
    return (
        <div style={box}>
            <select value={country.iso} onChange={onPick} disabled={disabled} aria-label="Country code"
                className={selectClassName} style={{ ...(selectClassName ? {} : DEFAULT_SELECT), ...selectStyle, flex: "0 0 auto", width: 98, cursor: "pointer", paddingLeft: 6, paddingRight: 4 }}>
                {COUNTRIES.map((c) => <option key={c.iso} value={c.iso}>{c.flag} +{c.code}{c.iso === "US" ? " US/CA" : ""}</option>)}
            </select>
            <input id={id} name={name} value={local} onChange={onType} disabled={disabled} autoFocus={autoFocus}
                type="tel" inputMode="tel" autoComplete="tel-national" aria-label={ariaLabel}
                placeholder={placeholder || (country.iso === "IN" ? "98765 43210" : "Mobile number")}
                className={className} style={{ ...(className ? {} : DEFAULT_INPUT), ...inputStyle, flex: 1, minWidth: 0, width: "auto" }} />
        </div>
    );
}

const DEFAULT_INPUT = { height: 44, border: "1.5px solid #E2E8F0", borderRadius: 11, padding: "0 12px", fontSize: 15, fontFamily: "inherit", color: "#0F172A", outline: "none", background: "#fff", boxSizing: "border-box" };
const DEFAULT_SELECT = { height: 44, border: "1.5px solid #E2E8F0", borderRadius: 11, padding: "0 6px", fontSize: 14, fontFamily: "inherit", color: "#0F172A", background: "#fff", boxSizing: "border-box" };
